import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import useDialogStyles from "../TransferTicketModalCustom/dialogStyles";

const useStyles = makeStyles((theme) => {
	const t = getTokens(theme.palette.type, theme.palette.primary.main);
	const isDark = theme.palette.type === "dark";
	// mesmos tons da busca do painel de conversas (padrão do WhatsApp Web)
	const wa = isDark
		? { field: "#202c33", focus: "#00a884", check: "#00a884" }
		: { field: "#f0f2f5", focus: "#00a884", check: "#00a884" };
	return {
		searchRow: {
			padding: "8px 22px 10px",
			flex: "none",
		},
		searchBox: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			height: 38,
			padding: "0 12px 0 14px",
			borderRadius: 19,
			backgroundColor: wa.field,
			border: "1px solid transparent",
			transition: "border-color 150ms ease, background-color 150ms ease",
			"&:focus-within": {
				backgroundColor: t.paper,
				borderColor: wa.focus,
				boxShadow: `0 0 0 1px ${wa.focus}`,
			},
		},
		searchIcon: {
			color: t.textMuted,
			fontSize: 18,
			flex: "none",
		},
		searchInput: {
			flex: 1,
			minWidth: 0,
			fontSize: 13.5,
			color: t.text,
			"& input::placeholder": {
				color: t.textMuted,
				opacity: 1,
			},
		},
		list: {
			flex: "1 1 auto",
			height: 320,
			maxHeight: "50vh",
			minHeight: 140,
			overflowY: "auto",
			borderTop: `1px solid ${t.line}`,
			...theme.scrollbarStyles,
		},
		row: {
			display: "flex",
			alignItems: "center",
			gap: 12,
			width: "100%",
			padding: "9px 22px",
			border: 0,
			background: "transparent",
			textAlign: "left",
			font: "inherit",
			color: "inherit",
			cursor: "pointer",
			transition: "background-color 120ms ease",
			"&:hover": { backgroundColor: t.muted },
			"&:focus-visible": {
				outline: `2px solid ${wa.focus}`,
				outlineOffset: -2,
			},
		},
		rowSelected: {
			backgroundColor: t.muted,
		},
		avatar: {
			width: 40,
			height: 40,
			flex: "none",
			// enquanto a foto carrega o círculo já aparece
			backgroundColor: t.status.neutral.bg,
			color: t.status.neutral.fg,
		},
		rowText: {
			flex: 1,
			minWidth: 0,
		},
		rowName: {
			fontSize: 14.5,
			lineHeight: "20px",
			color: t.text,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		rowNumber: {
			fontSize: 12.5,
			lineHeight: "17px",
			color: t.textSecondary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		check: {
			flex: "none",
			fontSize: 22,
			color: t.lineStrong,
		},
		checkOn: {
			color: wa.check,
		},
		addIcon: {
			width: 40,
			height: 40,
			flex: "none",
			borderRadius: "50%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: wa.field,
			color: t.textSecondary,
			"& svg": { fontSize: 20 },
		},
		// faixa com o contato escolhido, como o rodapé do "Encaminhar para" do WhatsApp
		selectionBar: {
			flex: "none",
			display: "flex",
			alignItems: "center",
			gap: 12,
			minHeight: 44,
			padding: "6px 22px",
			borderTop: `1px solid ${t.line}`,
		},
		footerInfo: {
			flex: 1,
			minWidth: 0,
			display: "flex",
			flexDirection: "column",
			gap: 1,
		},
		footerSelected: {
			fontSize: 13,
			fontWeight: 600,
			lineHeight: "18px",
			color: t.text,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		footerMuted: {
			fontWeight: 400,
			color: t.textSecondary,
		},
		sending: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			fontSize: 12,
			color: t.textSecondary,
		},
		sign: {
			margin: 0,
			color: t.textSecondary,
			"& .MuiFormControlLabel-label": { fontSize: 12.5 },
		},
		send: {
			minWidth: 0,
			"& .MuiButton-endIcon svg": { fontSize: 17 },
		},
	};
});

const ForwardMessageModal = ({ messages, onClose, modalOpen }) => {
	const classes = useStyles();
	const dialogClasses = useDialogStyles({ width: 440 });
	const [optionsContacts, setOptionsContacts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [sending, setSending] = useState(false);
	const [messageSending, setMessageSending] = useState('');
	const [signMessage, setSignMessage] = useState(true);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptionsContacts(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const history = useHistory();

	const sleep = (ms) => {
		return new Promise(resolve => setTimeout(resolve, ms));
	};

	const handleForwardMessage = async (contactL) => {
		const responseList = [];
		for (const message of messages) {
			setSending(true);
			try {
				setMessageSending(message.id);
				const response = await api.post('/message/forward', { messageId: message.id, contactId: contactL.id, signMessage: signMessage });
				responseList.push(response);
				sleep(900);
			} catch (error) {
				toastError(error);
			}
		}
		setSending(false);

		history.push('/tickets');
	}

	// contato sem número não pode receber: abre o cadastro, como já fazia o seletor antigo
	const handleSelectContact = (contact, selected) => {
		if (contact?.number) {
			setSelectedContact(selected ? null : contact);
		} else if (contact?.name) {
			setNewContact({ name: contact.name });
			setContactModalOpen(true);
		}
	};

	const handleAddContact = () => {
		setNewContact({ name: searchParam.trim() });
		setContactModalOpen(true);
	};

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setSending(false);
	};

	const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const searching = searchParam.length >= 3;
	const total = Array.isArray(messages) ? messages.length : 0;

	const renderList = () => {
		if (!searching) {
			return (
				// TODO i18n
				<div className={dialogClasses.empty}>
					Digite ao menos 3 letras do nome ou do número para buscar.
				</div>
			);
		}
		if (loading && optionsContacts.length === 0) {
			return (
				<div className={dialogClasses.loading}>
					<CircularProgress color="inherit" size={18} />
				</div>
			);
		}
		return (
			<>
				{optionsContacts.map((contact) => {
					const selected = selectedContact?.id === contact.id;
					return (
						<button
							type="button"
							key={contact.id}
							role="option"
							aria-selected={selected}
							className={clsx(classes.row, selected && classes.rowSelected)}
							onClick={() => handleSelectContact(contact, selected)}
						>
							<Avatar src={contact.urlPicture || undefined} className={classes.avatar}>
								{String(contact.name || "?").trim().charAt(0).toUpperCase()}
							</Avatar>
							<span className={classes.rowText}>
								<span className={classes.rowName} style={{ display: "block" }}>{contact.name}</span>
								<span className={classes.rowNumber} style={{ display: "block" }}>{contact.number}</span>
							</span>
							{selected ? (
								<CheckCircleIcon className={clsx(classes.check, classes.checkOn)} />
							) : (
								<RadioButtonUncheckedIcon className={classes.check} />
							)}
						</button>
					);
				})}
				{!loading && optionsContacts.length === 0 && (
					// TODO i18n
					<div className={dialogClasses.empty}>Nenhum contato encontrado.</div>
				)}
				{!loading && searchParam.trim() !== "" && (
					<button type="button" className={classes.row} onClick={handleAddContact}>
						<span className={classes.addIcon}><PersonAddIcon /></span>
						<span className={classes.rowText}>
							<span className={classes.rowName} style={{ display: "block" }}>
								{`${i18n.t("newTicketModal.add")} ${searchParam.trim()}`}
							</span>
							{/* TODO i18n */}
							<span className={classes.rowNumber} style={{ display: "block" }}>Cadastrar um novo contato</span>
						</span>
					</button>
				)}
			</>
		);
	};

	return (
		<>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
			></ContactModal>
			<Dialog
				open={modalOpen}
				onClose={handleClose}
				scroll="paper"
				classes={{ paper: dialogClasses.paper }}
				aria-labelledby="forward-message-title"
			>
				<div className={dialogClasses.header}>
					<h2 id="forward-message-title" className={dialogClasses.title}>
						Encaminhar mensagem
					</h2>
					{/* TODO i18n */}
					<p className={dialogClasses.subtitle}>
						{total > 1
							? `${total} mensagens selecionadas. Escolha para quem enviar.`
							: "Escolha para quem enviar."}
					</p>
				</div>
				<div className={classes.searchRow}>
					<div className={classes.searchBox}>
						<SearchIcon className={classes.searchIcon} />
						<InputBase
							className={classes.searchInput}
							placeholder={i18n.t("newTicketModal.fieldLabel")}
							value={searchParam}
							onChange={(e) => setSearchParam(e.target.value)}
							autoFocus
							inputProps={{ "aria-label": i18n.t("newTicketModal.fieldLabel") }}
						/>
						{loading && <CircularProgress color="inherit" size={16} />}
					</div>
				</div>
				<div className={classes.list} role="listbox" aria-label="Contatos">
					{renderList()}
				</div>
				<div className={classes.selectionBar}>
					<div className={classes.footerInfo}>
						<span className={clsx(classes.footerSelected, !selectedContact && classes.footerMuted)}>
							{/* TODO i18n */}
							{selectedContact ? selectedContact.name : "Nenhum contato escolhido"}
						</span>
						{sending && (
							<span className={classes.sending}>
								<CircularProgress color="inherit" size={12} />
								Enviando {messageSending}...
							</span>
						)}
					</div>
					<FormControlLabel
						className={classes.sign}
						label={i18n.t("messagesInput.signMessage")}
						labelPlacement="start"
						control={
							<Switch
								size="small"
								checked={signMessage}
								onChange={(e) => {
									setSignMessage(e.target.checked);
								}}
								name="showAllTickets"
								color="primary"
							/>
						}
					/>
				</div>
				<DialogActions className={dialogClasses.actions}>
					<Button onClick={handleClose} disabled={sending} className={dialogClasses.cancel}>
						{i18n.t("newTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={!selectedContact || sending}
						onClick={() => handleForwardMessage(selectedContact)}
						color="primary"
						loading={sending}
						className={clsx(dialogClasses.confirm, classes.send)}
						endIcon={<SendIcon />}
					>
						Encaminhar
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ForwardMessageModal;
